import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionToken } from "../utils/auth";
import { hasSeenNotifyIntro, markNotifyIntroSeen } from "../utils/introSeen";

// One-time announcement that availability notifications exist.
//
// Shown once per browser. Two dismissals with different meanings:
//   - "Got it"      -> remembered, never shown again
//   - "Remind me later" -> not remembered, shows again next visit
// so the persistent choice is a real choice rather than the only option.
//
// Skipped entirely for signed-in users (they already use the feature) and while a
// profile dialog is open (don't stack modals on someone mid-task).
function NotifyIntroModal() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (hasSeenNotifyIntro()) return;
    if (getSessionToken()) return;
    if (window.location.hash.startsWith("#profile-")) return;

    // Let the roster paint first; an announcement over a loading screen has no context.
    const timer = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const dismissForever = useCallback(() => {
    markNotifyIntroSeen();
    setShow(false);
  }, []);

  const dismissForNow = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") dismissForever();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [show, dismissForever]);

  if (!show) return null;

  const handleSetUp = () => {
    markNotifyIntroSeen();
    setShow(false);
    navigate("/notifications");
  };

  return (
    <div
      onClick={(e) => {
        // Clicking the backdrop counts as "seen" - it's an acknowledgement, and
        // re-nagging someone who clearly closed it is worse than losing the message.
        if (e.target === e.currentTarget) dismissForever();
      }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9998,
        padding: "20px",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="notify-intro-title"
        className="card shadow"
        style={{ maxWidth: 420, width: "100%" }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h2 id="notify-intro-title" className="h5 mb-0">
              🔔 New: get told when someone's back
            </h2>
            <button
              type="button"
              className="btn-close"
              onClick={dismissForever}
              aria-label="Close"
            />
          </div>

          <p className="mb-2">
            Tap the <strong>🔕 bell</strong> next to any name in the roster to follow
            them. We'll email you when they appear on <strong>tomorrow's</strong> roster.
          </p>
          <ul className="small text-muted ps-3 mb-3">
            <li>One email a night at 7:30pm, covering everyone you follow</li>
            <li>Sign in with just your email — no password</li>
            <li>Unfollow anytime from the Notifications page</li>
          </ul>

          {/* Stacked rather than side by side: at 390px the two secondary labels
              both wrap and end up cramped. */}
          <div className="d-grid gap-2">
            <button type="button" className="btn btn-primary" onClick={handleSetUp}>
              Set up notifications
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={dismissForever}
            >
              Got it, don't show again
            </button>
            {/* Bootstrap's .text-muted is close to unreadable on this dark theme, so
                use the theme's own secondary text colour instead. */}
            <button
              type="button"
              onClick={dismissForNow}
              style={{
                background: "none",
                border: "none",
                padding: "4px",
                fontSize: "0.9rem",
                color: "var(--text-secondary)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotifyIntroModal;
